import { useRef, useState } from 'react';
import api from '../services/api';
import { errorMessage } from '../lib/http';
import { IconUpload } from './NavIcons';
import { Spinner } from './ui';

export const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt';

export default function UploadButton({
  code,
  title,
  remediationItemId,
  label = 'Upload',
  primary,
  onUploaded,
  onError,
}: {
  code: string;
  title?: string;
  remediationItemId?: string;
  label?: string;
  primary?: boolean;
  onUploaded: () => void;
  onError: (message: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    if (file.size === 0) {
      onError('This file is empty. Choose a document with content.');
      return;
    }
    if (file.size > 40 * 1024 * 1024) {
      onError('Files must be 40 MB or smaller.');
      return;
    }
    if (/(sample|dummy|placeholder|template)/i.test(file.name)) {
      onError(`“${file.name}” looks like a sample, dummy, placeholder or template. Upload the completed original document.`);
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append('code', code);
      if (title) body.append('title', title);
      if (remediationItemId) body.append('remediationItemId', remediationItemId);
      body.append('file', file);
      const { data } = await api.post<{ warnings?: string[] }>('/customer/documents', body, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      if (data.warnings?.length) onError(`Uploaded with warning: ${data.warnings.join(' ')}`);
      onUploaded();
    } catch (err) {
      onError(errorMessage(err, 'Upload failed.'));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
        }}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => input.current?.click()}
        className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold disabled:opacity-60 ${
          primary ? 'mz-btn-primary text-white' : 'text-[#d6006e] hover:bg-[#fce7f3]'
        }`}
        style={primary ? { background: '#d6006e', boxShadow: '0 6px 18px rgba(214,0,110,0.25)' } : { background: '#fdf2f8', border: '1px solid #fce7f3' }}
      >
        {busy ? <Spinner className="h-3.5 w-3.5" /> : <IconUpload className="h-3.5 w-3.5" />}
        {busy ? 'Uploading…' : label}
      </button>
    </>
  );
}
